import * as pc from 'playcanvas';
import type { SubjectCandidate, SubjectVector } from '../utils/subjectBounds';

export interface SubjectCameraSuggestion {
    position: SubjectVector;
    target: SubjectVector;
    visibility: number;
    coverage: number;
    completeness: number;
    evaluatedViews: number;
    elapsedMs: number;
}
export interface SubjectCameraOptions {
    signal?: AbortSignal;
    isCurrent?: () => boolean;
    requestRender?: () => void;
    maxTimeMs?: number;
}
interface ImageStats { sum: number; coverage: number; edge: number; gradient: number }
const MASK_GLSL = /* glsl */`
uniform vec3 subjectMin;
uniform vec3 subjectMax;
uniform float subjectOnly;
uniform float subjectColor;
void modifySplatCenter(inout vec3 center) {}
void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {}
void modifySplatColor(vec3 center, inout vec4 color) {
    bool inside = all(greaterThanEqual(center, subjectMin)) && all(lessThanEqual(center, subjectMax));
    if (subjectColor < .5) color.rgb = inside ? vec3(0., 1., 0.) : vec3(1., 0., 0.);
    if (!inside && subjectOnly > .5) color.a = 0.;
}
`;
const MASK_WGSL = /* wgsl */`
uniform subjectMin: vec3f;
uniform subjectMax: vec3f;
uniform subjectOnly: f32;
uniform subjectColor: f32;
fn modifySplatCenter(center: ptr<function, vec3f>) {}
fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {}
fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    let inside = all(center >= uniform.subjectMin) && all(center <= uniform.subjectMax);
    if (uniform.subjectColor < .5) { (*color).rgb = select(vec3f(1., 0., 0.), vec3f(0., 1., 0.), inside); }
    if (!inside && uniform.subjectOnly > .5) { (*color).a = 0.; }
}
`;

export function subjectImageStats(pixels: Uint8Array, width: number, height: number): ImageStats {
    let sum = 0, edge = 0, gradient = 0, pairs = 0;
    const mx = Math.ceil(width * .04), my = Math.ceil(height * .04);
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        const at = (y * width + x) * 4, green = pixels[at + 1] / 255;
        sum += green;
        if (x < mx || x >= width - mx || y < my || y >= height - my) edge += green;
        if (x && y && pixels[at + 3] > 128 && pixels[at - 1] > 128 && pixels[at - width * 4 + 3] > 128) {
            for (let k = 0; k < 3; k++) { gradient += Math.abs(pixels[at + k] - pixels[at - 4 + k]) + Math.abs(pixels[at + k] - pixels[at - width * 4 + k]); pairs += 2; }
        }
    }
    return { sum, coverage: sum / (width * height), edge: edge / Math.max(sum, 1), gradient: gradient / Math.max(pairs, 1) / 255 };
}

/** Reject occluded/clipped guesses instead of replacing a camera with an AABB fit. */
export function scoreSubjectView(candidate: SubjectCandidate, actual: ImageStats, alone: ImageStats, detail: ImageStats, completeness: number): number {
    const visible = Math.min(1, actual.sum / Math.max(alone.sum, 1));
    if (completeness < .97 || visible < .65 || actual.coverage < .025 || alone.edge > .12) return 0;
    return Math.sqrt(candidate.seedShare / candidate.seedRadius ** 2)
        * Math.exp(-Math.abs(Math.log(actual.coverage / .30)) * .65)
        * visible ** 2 * (1 - actual.edge) * (1 - alone.edge)
        * Math.sqrt(Math.max(.15, Math.min(1, detail.gradient / .025)));
}

/**
 * Search an isolated proxy layer; the user's camera, scene materials and splat
 * resource are never edited. A suggestion is applied by the editor only after
 * its import token and user camera pose have been rechecked.
 */
export async function findSubjectCamera(
    app: pc.AppBase, source: pc.Entity, mainCamera: pc.Entity, candidates: SubjectCandidate[], options: SubjectCameraOptions = {},
): Promise<SubjectCameraSuggestion | null> {
    const resource = source.gsplat?.resource, main = mainCamera.camera;
    if (!resource || !main || main.projection !== pc.PROJECTION_PERSPECTIVE || !candidates.length) return null;
    const started = performance.now();
    const aspect = Math.max(.2, Math.min(5, app.graphicsDevice.width / Math.max(1, app.graphicsDevice.height)));
    const width = Math.max(32, Math.round(192 * Math.min(1, aspect))), height = Math.max(32, Math.round(192 * Math.min(1, 1 / aspect)));
    const layer = new pc.Layer({ name: 'ImportSubjectAnalysis' });
    const proxy = new pc.Entity('import-subject-analysis-proxy'), camera = new pc.Entity('import-subject-analysis-camera');
    let texture: pc.Texture | undefined, target: pc.RenderTarget | undefined;
    const current = (): boolean => !options.signal?.aborted && (options.isCurrent?.() ?? true);
    const frame = (): Promise<void> => new Promise((resolve, reject) => {
        if (!current()) { reject(new Error('Import framing cancelled')); return; }
        const finish = (): void => { cleanup(); current() ? resolve() : reject(new Error('Import framing cancelled')); };
        const abort = (): void => { cleanup(); reject(new Error('Import framing cancelled')); };
        const timeout = setTimeout(abort, 1000);
        const cleanup = (): void => { clearTimeout(timeout); app.off('postrender', finish); options.signal?.removeEventListener('abort', abort); };
        app.once('postrender', finish);
        options.signal?.addEventListener('abort', abort, { once: true });
        app.renderNextFrame = true;
        options.requestRender?.();
    });
    let best: SubjectCameraSuggestion | null = null, bestScore = 0, evaluatedViews = 0;
    try {
        app.scene.layers.push(layer);
        source.addChild(proxy);
        proxy.addComponent('gsplat', { resource, unified: false, layers: [layer.id] } as never);
        texture = new pc.Texture(app.graphicsDevice, { name: 'import-subject-analysis', width, height, format: pc.PIXELFORMAT_RGBA8, mipmaps: false, minFilter: pc.FILTER_NEAREST, magFilter: pc.FILTER_NEAREST });
        target = new pc.RenderTarget({ colorBuffer: texture, depth: false });
        app.root.addChild(camera);
        camera.addComponent('camera', { layers: [layer.id], clearColor: new pc.Color(0, 0, 0, 0), renderTarget: target, fov: main.fov, horizontalFov: main.horizontalFov, nearClip: Math.min(.01, main.nearClip), farClip: main.farClip, priority: -100 } as never);
        const material = proxy.gsplat!.material;
        if (!material) return null;
        const lang = app.graphicsDevice.isWebGPU ? 'wgsl' : 'glsl';
        material.getShaderChunks(lang).set('gsplatModifyVS', lang === 'wgsl' ? MASK_WGSL : MASK_GLSL);
        material.update();
        material.setParameter('subjectOnly', 0); material.setParameter('subjectColor', 0);
        material.setParameter('subjectMin', [-1, -1, -1]); material.setParameter('subjectMax', [1, 1, 1]);
        const capture = async (only: number, color = 0): Promise<ImageStats> => {
            material.setParameter('subjectOnly', only); material.setParameter('subjectColor', color);
            await frame();
            const pixels = await texture!.read(0, 0, width, height, { renderTarget: target, immediate: true } as never) as Uint8Array;
            if (!current()) throw new Error('Import framing cancelled');
            return subjectImageStats(pixels, width, height);
        };
        const world = source.getWorldTransform().clone(), scale = world.getScale(new pc.Vec3());
        const radiusScale = Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z));
        const tanV = Math.tan(main.fov * Math.PI / 360) / (main.horizontalFov ? aspect : 1), tanH = tanV * aspect;
        search: for (const candidate of candidates.slice(0, 4)) {
            if (!current()) return null;
            const center = world.transformPoint(new pc.Vec3(...candidate.center)), direction = world.transformVector(new pc.Vec3(...candidate.direction)).normalize();
            const baseYaw = Math.atan2(direction.x, direction.z);
            const samples = candidate.samples.map(p => world.transformPoint(new pc.Vec3(...p)));
            material.setParameter('subjectMin', candidate.lo); material.setParameter('subjectMax', candidate.hi);
            for (const delta of [0, Math.PI / 2, Math.PI, Math.PI * 1.5]) for (const factor of [1.35, 2.15, 3]) {
                if (!current()) return null;
                if (performance.now() - started > (options.maxTimeMs ?? 6500)) break search;
                const yaw = baseYaw + delta;
                // Keep the same angular sampling when FOV or viewport aspect changes.
                const distance = candidate.radius * radiusScale * factor * Math.tan(Math.PI / 6) / Math.min(tanV, tanH);
                const position = center.clone().add(new pc.Vec3(Math.sin(yaw) * distance, distance * .08, Math.cos(yaw) * distance));
                camera.setPosition(position); camera.lookAt(center);
                await frame(); await frame();
                let included = 0;
                for (const p of samples) {
                    const offset = p.clone().sub(position), depth = offset.dot(camera.forward);
                    if (depth > 0 && Math.abs(offset.dot(camera.right) / (depth * tanH)) < .86 && Math.abs(offset.dot(camera.up) / (depth * tanV)) < .86) included++;
                }
                const completeness = included / samples.length;
                if (completeness < .97) continue;
                const actual = await capture(0), alone = await capture(1), detail = await capture(1, 1);
                evaluatedViews++;
                const score = scoreSubjectView(candidate, actual, alone, detail, completeness);
                if (score > bestScore) {
                    bestScore = score;
                    best = { position: [position.x, position.y, position.z], target: [center.x, center.y, center.z], visibility: Math.min(1, actual.sum / Math.max(alone.sum, 1)), coverage: actual.coverage, completeness, evaluatedViews, elapsedMs: performance.now() - started };
                }
            }
        }
        return current() && best ? { ...best, evaluatedViews, elapsedMs: performance.now() - started } : null;
    } catch {
        // Unsupported resource/backend, context loss, cancellation or a render
        // timeout preserve the existing camera. No raw-AABB fallback is applied.
        return null;
    } finally {
        camera.destroy(); proxy.destroy(); app.scene.layers.remove(layer);
        target?.destroy(); texture?.destroy();
    }
}
